import { type FC } from 'react'
import { Card } from 'react-bootstrap'
import classNames from 'classnames'
import { PieChart } from 'react-minimal-pie-chart'
import { SCORE_BENCHMARK } from '../scoreRoster'
import { RANGE_PERIOD, type RangeKey } from '../userScoreRange'
import styles from './UserScoreCard.module.scss'

interface UserScoreCardProps {
  score: number | null
  delta: number | null
  range: RangeKey
}

export const UserScoreCard: FC<UserScoreCardProps> = ({ score, delta, range }) => {
  const roundedScore = score === null ? null : Math.round(score)
  const roundedDelta = delta === null ? null : Math.round(delta)
  // Coloring rule: below 50 red, below the benchmark amber, at or above it green
  const ringColor =
    roundedScore !== null && roundedScore < 50
      ? 'var(--zocks-danger)'
      : roundedScore !== null && roundedScore < SCORE_BENCHMARK
        ? 'var(--zocks-warning)'
        : 'var(--zocks-success)'

  return (
    <Card className='h-100 zocks-border-color-gray-200 zocks-border-0.75'>
      <Card.Body className='d-flex flex-column p-4 zocks-gap-1.5'>
        <h5 className='insights-section-title mb-0'>Meeting score</h5>
        {roundedScore === null ? (
          <div className='flex-grow-1 d-flex align-items-center justify-content-center paragraph-medium zocks-text-gray-600'>Not enough data</div>
        ) : (
          <div className='flex-grow-1 d-flex flex-column align-items-center justify-content-center zocks-gap-1.5'>
            <div className='position-relative'>
              <PieChart
                data={[{ title: 'Score', value: roundedScore, color: ringColor }]}
                background='var(--zocks-gray-200)'
                lineWidth={15}
                rounded
                totalValue={100}
                startAngle={270}
                className={styles.scoreRing}
              />
              <div className='position-absolute top-0 start-0 d-flex align-items-center justify-content-center w-100 h-100'>
                <span className={classNames('bold text-black', styles.scoreValue, styles.tabularNums)}>{roundedScore}</span>
              </div>
            </div>
            <div className='paragraph-medium zocks-text-gray-900'>
              {roundedDelta === null ? (
                <span className='zocks-text-gray-600'>No scores in the previous {RANGE_PERIOD[range]}</span>
              ) : (
                <>
                  <span className={classNames('bold', styles.tabularNums, roundedDelta >= 0 ? styles.deltaUp : styles.deltaDown)}>
                    {roundedDelta >= 0 ? `+${roundedDelta}` : roundedDelta}
                  </span>{' '}
                  vs previous {RANGE_PERIOD[range]}
                </>
              )}
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  )
}
